import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  getFirestore,
} from 'firebase/firestore';
import type { Supplier, SupplierFormData } from '../types/supplier';

const formatSupplierForFirestore = (supplier: Partial<SupplierFormData>) => ({
  ...supplier,
  name: supplier.name?.trim(),
  contactPerson: supplier.contactPerson?.trim() || null,
  phone: supplier.phone?.trim() || null,
  email: supplier.email?.trim() || null,
  address: supplier.address?.trim() || null,
  gstNumber: supplier.gstNumber?.trim() || null,
  notes: supplier.notes?.trim() || null,
  updatedAt: serverTimestamp(),
});

export const getSuppliers = async (restId: string): Promise<Supplier[]> => {
  const db = getFirestore();
  try {
    const suppliersRef = collection(db, 'suppliers');
    const q = query(
      suppliersRef,
      where('restId', '==', restId),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (d) =>
        ({
          id: d.id,
          ...d.data(),
        }) as Supplier
    );
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw new Error('Failed to fetch suppliers. Please try again later.');
  }
};

export const addSupplier = async (supplier: SupplierFormData): Promise<string> => {
  const db = getFirestore();
  try {
    const suppliersRef = collection(db, 'suppliers');
    const docRef = await addDoc(suppliersRef, {
      ...formatSupplierForFirestore(supplier),
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding supplier:', error);
    throw new Error('Failed to add supplier. Please try again later.');
  }
};

export const updateSupplier = async (
  id: string,
  supplier: Partial<SupplierFormData>
): Promise<void> => {
  const db = getFirestore();
  try {
    const supplierRef = doc(db, 'suppliers', id);
    await updateDoc(supplierRef, formatSupplierForFirestore(supplier));
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw new Error('Failed to update supplier. Please try again later.');
  }
};

export const deleteSupplier = async (id: string): Promise<void> => {
  const db = getFirestore();
  try {
    const supplierRef = doc(db, 'suppliers', id);
    await deleteDoc(supplierRef);
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw new Error('Failed to delete supplier. Please try again later.');
  }
};
